import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { absoluteUrl, breadcrumbJsonLd, usePageMeta } from '../../core/usePageMeta';
import { PRODUCTS, productPath } from './products';
import { ROUTES } from '../../app/routes';
import { useAppSelector } from '../../app/store';
import { ApplyDialog } from '../hub/ApplyDialog';
import { isNotFound, publicApi, type JobCard, type JobDetail, type Paged } from './publicApi';
import { companyPath, openCompany, EmptyState, employmentLabel, ErrorState, formatMoney, JobCardView, LoadingGrid, PublicLayout, usePublicLanguage } from './components';

/** `/jobs` — open positions of every listed organization (Hub design 06). */
export function JobsPage() {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const [params, setParams] = useSearchParams();
  const listPage = Math.max(1, Number(params.get('page')) || 1);
  usePageMeta(t('hub.seo.jobs.title', 'Jobs in Albania'), {
    description: t('hub.seo.jobs.description'),
    // Searches are noindex; the plain list (and its pages) is the one canonical jobs listing.
    noindex: Boolean(params.get('q') || params.get('city')),
    path: listPage > 1 ? `${ROUTES.jobs}?page=${listPage}` : ROUTES.jobs,
    lang,
    jsonLd: breadcrumbJsonLd([{ name: t('hub.seo.breadcrumbHome', 'Home'), path: '/' }, { name: t('hub.nav.jobs', 'Jobs'), path: ROUTES.jobs }]),
  });
  const [result, setResult] = useState<Paged<JobCard> | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [q, setQ] = useState(params.get('q') ?? '');
  const [city, setCity] = useState(params.get('city') ?? '');
  const page = Math.max(1, Number(params.get('page')) || 1);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(false);
    publicApi.jobs(lang, { q: params.get('q') ?? undefined, city: params.get('city') ?? undefined, page, pageSize: 24 })
      .then((data) => { if (active) setResult(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [lang, params, page, attempt]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (city.trim()) next.set('city', city.trim());
    setParams(next);
  }

  const pages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;
  function pageHref(target: number) {
    const next = new URLSearchParams(params);
    if (target > 1) next.set('page', String(target)); else next.delete('page');
    const query = next.toString();
    return `${ROUTES.jobs}${query ? `?${query}` : ''}`;
  }
  return <PublicLayout>
    <div className="hub-wrap hub-section">
      <div className="hub-section-head"><h1 className="hub-page-title">{t('hub.jobs.title', 'Jobs')}</h1>
        {result && <span className="hub-muted">{t('hub.jobs.count', '{{count}} open positions', { count: result.total })}</span>}</div>
      <form className="hub-search" role="search" onSubmit={submit} style={{ maxWidth: 760, marginBottom: 22 }}>
        <label className="hub-sr" htmlFor="hub-job-q">{t('hub.jobs.what', 'Job, business or role')}</label>
        <input id="hub-job-q" value={q} onChange={(event) => setQ(event.target.value)} placeholder={t('hub.jobs.what', 'Job, business or role')} />
        <label className="hub-sr" htmlFor="hub-job-city">{t('hub.search.where', 'City')}</label>
        <input id="hub-job-city" value={city} onChange={(event) => setCity(event.target.value)} placeholder={t('hub.search.where', 'City')} />
        <button type="submit">{t('hub.search.submit', 'Search')}</button>
      </form>
      {error && <ErrorState message={t('hub.jobs.loadError', 'Jobs could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
      {!result && !error && <LoadingGrid label={t('hub.loading', 'Loading')} />}
      {result && result.items.length === 0 && <EmptyState>{t('hub.landing.noJobs', 'No open positions right now.')}</EmptyState>}
      {result && result.items.length > 0 && <div className="hub-grid">{result.items.map((job) => <JobCardView key={job.id} job={job} />)}</div>}
      {result && pages > 1 && <nav className="hub-pager" aria-label={t('hub.search.pages', 'Pages')}>
        {page > 1
          ? <Link className="hub-btn sm ghost" to={pageHref(page - 1)} rel="prev">{t('hub.search.previous', 'Previous')}</Link>
          : <span className="hub-btn sm ghost" aria-disabled="true">{t('hub.search.previous', 'Previous')}</span>}
        <span>{t('hub.search.pageOf', 'Page {{page}} of {{pages}}', { page, pages })}</span>
        {page < pages
          ? <Link className="hub-btn sm ghost" to={pageHref(page + 1)} rel="next">{t('hub.search.next', 'Next')}</Link>
          : <span className="hub-btn sm ghost" aria-disabled="true">{t('hub.search.next', 'Next')}</span>}
      </nav>}
    </div>
  </PublicLayout>;
}

const EMPLOYMENT_TYPES: Record<string, string> = {
  FullTime: 'FULL_TIME', PartTime: 'PART_TIME', Freelance: 'CONTRACTOR', ChairRental: 'CONTRACTOR', Seasonal: 'TEMPORARY', Internship: 'INTERN',
};
const SALARY_UNITS: Record<string, string> = { Hour: 'HOUR', Month: 'MONTH', Year: 'YEAR' };

function escapeHtml(text: string) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** schema.org `JobPosting` — what makes a vacancy eligible for Google for Jobs. */
export function jobPostingJsonLd(detail: JobDetail, path: string) {
  const { job } = detail;
  const sections = [detail.description, detail.requirements, detail.benefits].filter((text): text is string => Boolean(text?.trim()));
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: (sections.length ? sections : [job.title]).map((text) => `<p>${escapeHtml(text).replace(/\r?\n/g, '<br>')}</p>`).join(''),
    url: absoluteUrl(path),
    identifier: { '@type': 'PropertyValue', name: job.organizationName, value: job.id },
    ...(job.publishedAt ? { datePosted: job.publishedAt.slice(0, 10) } : {}),
    ...(detail.closesOn ? { validThrough: `${detail.closesOn.slice(0, 10)}T23:59:59` } : {}),
    ...(EMPLOYMENT_TYPES[job.employmentType] ? { employmentType: EMPLOYMENT_TYPES[job.employmentType] } : {}),
    hiringOrganization: {
      '@type': 'Organization',
      name: job.organizationName,
      ...(job.organizationSlug ? { sameAs: absoluteUrl(companyPath(job.organizationSlug)) } : {}),
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...(detail.addressLine ? { streetAddress: detail.addressLine } : {}),
        addressLocality: job.city,
        addressCountry: 'AL',
      },
    },
    ...(job.salaryMin != null && job.salaryCurrency ? {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: job.salaryCurrency,
        value: {
          '@type': 'QuantitativeValue',
          ...(job.salaryMax != null ? { minValue: job.salaryMin, maxValue: job.salaryMax } : { value: job.salaryMin }),
          unitText: SALARY_UNITS[job.salaryPeriod ?? ''] ?? 'MONTH',
        },
      },
    } : {}),
  };
}

/** `/jobs/:jobId` — job page. Online applications arrive with the jobs wave (BE-08 / FE-06). */
export function JobPage() {
  // HUB-UI: customers apply from the public vacancy; anonymous visitors sign in first.
  const signedIn = useAppSelector(state => state.session.status === 'authenticated');
  const [applying, setApplying] = useState(false);
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const { jobId = '' } = useParams();
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState('loading');
    publicApi.job(lang, jobId)
      .then((data) => { if (active) { setDetail(data); setState('ready'); } })
      .catch((error: unknown) => { if (active) setState(isNotFound(error) ? 'missing' : 'error'); });
    return () => { active = false; };
  }, [lang, jobId, attempt]);

  const job = detail?.job;
  const jobPath = ROUTES.job.replace(':jobId', jobId);
  usePageMeta(job ? t('hub.seo.job.title', '{{title}} — {{company}}, {{city}}', { title: job.title, company: job.organizationName, city: job.city }) : t('hub.jobs.title', 'Jobs'), {
    description: job ? t('hub.seo.job.description', { title: job.title, company: job.organizationName, city: job.city, text: (detail.description ?? '').slice(0, 110) }) : null,
    // A closed or unknown job must drop out of the index rather than linger as an empty page.
    noindex: state === 'missing',
    path: job ? jobPath : null,
    lang,
    jsonLd: job ? [
      breadcrumbJsonLd([
        { name: t('hub.seo.breadcrumbHome', 'Home'), path: '/' },
        { name: t('hub.nav.jobs', 'Jobs'), path: ROUTES.jobs },
        { name: job.title, path: jobPath },
      ]),
      jobPostingJsonLd(detail, jobPath),
    ] : null,
  });
  const salary = job && job.salaryMin != null
    ? [formatMoney(job.salaryMin, job.salaryCurrency, lang), job.salaryMax != null ? formatMoney(job.salaryMax, job.salaryCurrency, lang) : null].filter(Boolean).join(' – ')
    : null;

  return <PublicLayout>
    <div className="hub-wrap hub-section">
      <p><Link to={ROUTES.jobs}>← {t('hub.jobs.back', 'All jobs')}</Link></p>
      {state === 'loading' && <div className="hub-skeleton" role="status" aria-label={t('hub.loading', 'Loading')} style={{ minHeight: 240 }} />}
      {state === 'missing' && <EmptyState>{t('hub.jobs.notFound', 'This position is no longer open.')}</EmptyState>}
      {state === 'error' && <ErrorState message={t('hub.jobs.loadError', 'Jobs could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
      {state === 'ready' && detail && job && <div className="hub-company-body" style={{ paddingTop: 0 }}>
        <article className="hub-panel">
          <h1 className="hub-page-title" style={{ marginBottom: 6 }}>{job.title}</h1>
          <p className="hub-muted" style={{ marginTop: 0 }}>
            {job.organizationSlug
              ? <Link to={companyPath(job.organizationSlug)} onClick={(event) => openCompany(event, job.organizationSlug)}>{job.organizationName}</Link>
              : job.organizationName} · {job.city}
          </p>
          <p><span className="hub-pill job">{employmentLabel(t, job.employmentType)}</span></p>
          {detail.description && <><h2>{t('hub.jobs.description', 'Description')}</h2><p className="hub-prose">{detail.description}</p></>}
          {detail.requirements && <><h2>{t('hub.jobs.requirements', 'Requirements')}</h2><p className="hub-prose">{detail.requirements}</p></>}
          {detail.benefits && <><h2>{t('hub.jobs.benefits', 'Benefits')}</h2><p className="hub-prose">{detail.benefits}</p></>}
        </article>
        <aside className="hub-panel">
          <dl>
            {job.roleName && <><dt>{t('hub.jobs.role', 'Role')}</dt><dd>{job.roleName}</dd></>}
            {salary && <><dt>{t('hub.jobs.salary', 'Salary')}</dt><dd>{salary}</dd></>}
            {detail.addressLine && <><dt>{t('hub.company.address', 'Address')}</dt><dd>{detail.addressLine}</dd></>}
            {detail.closesOn && <><dt>{t('hub.jobs.closes', 'Closes')}</dt><dd>{detail.closesOn}</dd></>}
          </dl>
          {signedIn ? <button className="hub-btn" type="button" onClick={() => setApplying(true)}>{t('hub.jobs.apply', 'Apply for this position')}</button> : <Link className="hub-btn" to={ROUTES.login} state={{ from: `/me/applications?jobId=${encodeURIComponent(jobId)}` }}>{t('hub.jobs.signInApply', 'Sign in to apply')}</Link>}
          {job.organizationSlug && <Link className="hub-btn ghost" to={companyPath(job.organizationSlug)} onClick={(event) => openCompany(event, job.organizationSlug)}>{t('hub.jobs.viewCompany', 'View business')}</Link>}
        </aside>
      </div>}
      {applying && <ApplyDialog jobId={jobId} close={() => setApplying(false)} />}
    </div>
  </PublicLayout>;
}

/** `/for-business` — why list on Biznesi and where to start. */
export function ForBusinessPage() {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  usePageMeta(t('hub.seo.forBusiness.title', 'For business'), {
    description: t('hub.seo.forBusiness.description'),
    path: ROUTES.forBusiness,
    lang,
    jsonLd: breadcrumbJsonLd([{ name: t('hub.seo.breadcrumbHome', 'Home'), path: '/' }, { name: t('hub.nav.forBusiness', 'For business'), path: ROUTES.forBusiness }]),
  });
  // Every catalogue product links to its own page; pharmacy and auto service have none yet.
  const products: [string, string, string | null][] = [
    ...PRODUCTS.map((product): [string, string, string | null] => [
      t(`hub.products.items.${product.key}.name`, product.key), t(`hub.products.items.${product.key}.tagline`), productPath(product.slug),
    ]),
    [t('hub.business.pharmacyName', 'Pharmacy'), t('hub.business.pharmacy', 'Stock, sales and prescriptions.'), null],
    [t('hub.business.autoName', 'Auto service'), t('hub.business.auto', 'Work orders, parts and customers.'), null],
  ];
  return <PublicLayout>
    <section className="hub-hero"><div className="hub-wrap">
      <h1>{t('hub.business.title', 'Run your business on Biznesi')}</h1>
      <p>{t('hub.business.subtitle', 'One account and one company profile — listed in the Biznesi directory for every customer to find.')}</p>
      <Link className="hub-btn" to={ROUTES.register}>{t('hub.business.cta', 'Create an account')}</Link>
    </div></section>
    <div className="hub-wrap hub-section">
      <div className="hub-grid">
        {products.map(([name, text, path]) => <article key={name} className="hub-card"><div className="pad">
          <h3>{path ? <Link to={path}>{name}</Link> : name}</h3><span className="hub-muted">{text}</span>
        </div></article>)}
      </div>
      <div className="hub-banner">
        <div>
          <h2>{t('hub.business.alreadyTitle', 'Already a customer?')}</h2>
          <p>{t('hub.business.alreadyText', 'Sign in and complete your company profile to appear in the directory.')}</p>
        </div>
        <Link className="hub-btn" to={ROUTES.login}>{t('hub.nav.signIn', 'Sign in')}</Link>
      </div>
    </div>
  </PublicLayout>;
}
